<template>
  <div class="relative" data-test-id="fcc-alerts-container">
    <div
      class="px-4 pb-4 space-y-4 bg-surface-0 dark:bg-surface-900 rounded-lg border border-surface-200 dark:border-surface-700"
    >
      <div
        v-if="showHistoryToggle"
        class="pt-4 flex flex-wrap items-center gap-3"
        data-test-id="alerts-view-mode-toggle"
      >
        <SelectButton
          v-model="viewMode"
          :options="viewModeOptions"
          optionLabel="label"
          optionValue="value"
          :allowEmpty="false"
          size="small"
          data-test-id="alerts-view-mode"
        />
        <span class="text-xs text-surface-500 dark:text-surface-400">
          {{ viewModeCaption }}
        </span>
      </div>

      <AlertsFilters class="sticky top-[3.6rem] z-20 py-4 w-full">
        <!-- Custom refresh button based on context -->
        <template #refresh-button>
          <Button
            label="Refresh"
            @click="handleRefresh"
            icon="pi pi-refresh"
            size="small"
          />
        </template>
      </AlertsFilters>

      <!-- AlertsTable for alert history -->
      <AlertsTable
        v-if="effectiveIsAlertHistory"
        ref="alertsTableRef"
        class=""
        :merchantId="merchantId"
        :showMerchantColumn="showMerchantColumn"
        :alertsData="displayAlerts"
        :isAlertHistoryData="effectiveIsAlertHistory"
        :pagination="alertHistoryPagination"
        :paginationLoading="alertHistoryLoading"
        :initialLoading="alertHistoryLoading"
        @page-change="onPageChange"
        @page-size-change="onPageSizeChange"
        @action-completed="handleActionCompleted"
      />

      <template v-else-if="isCurrentEventsData">
        <AlertsTable
          ref="alertsTableRef"
          class=""
          :merchantId="merchantId"
          :showMerchantColumn="showMerchantColumn"
          :alertsData="merchantStore.currentMerchant?.CURRENT_EVENTS"
          :isAlertHistoryData="effectiveIsAlertHistory"
          :pagination="undefined"
          :paginationLoading="false"
          :initialLoading="loading"
          @page-change="onPageChange"
          @page-size-change="onPageSizeChange"
          @action-completed="handleActionCompleted"
        />
      </template>

      <!-- AlertsTable for regular alerts -->
      <template v-else>
        <AlertsTable
          ref="alertsTableRef"
          class=""
          :merchantId="merchantId"
          :showMerchantColumn="showMerchantColumn"
          :alertsData="displayAlerts"
          :isAlertHistoryData="effectiveIsAlertHistory"
          :pagination="undefined"
          :paginationLoading="false"
          :initialLoading="loading"
          @page-change="onPageChange"
          @page-size-change="onPageSizeChange"
          @action-completed="handleActionCompleted"
        />
      </template>

      <!-- Actions Bar -->
      <div
        v-if="canEdit && selectedAlerts.length > 0"
        class="sticky bottom-9 z-10 bg-surface-100 dark:bg-surface-800/20 rounded border border-surface-200 dark:border-surface-700 px-4 py-2 shadow-xl"
      >
        <div class="flex items-center justify-between">
          <span class="text-sm text-surface-700 dark:text-surface-200">
            {{ selectedAlerts.length }} alert{{
              selectedAlerts.length > 1 ? 's' : ''
            }}
            selected
          </span>
          <div class="flex items-center gap-2">
            <Button
              v-if="nonIssueActionAvailable"
              v-tooltip.top="'Mark as Non-Issue'"
              :label="`Mark ${selectedAlerts.length} as Non-Issue`"
              icon="pi pi-check-circle"
              size="small"
              :loading="isBulkNonIssueSubmitting"
              :disabled="isBulkNonIssueSubmitting"
              @click="confirmBulkNonIssue"
              data-test-id="non-issue-bulk-action"
            />
            <Button
              label="Take Action"
              icon="pi pi-bolt"
              size="small"
              @click="openBulkActionDrawer"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import Button from 'primevue/button'
import SelectButton from 'primevue/selectbutton'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'
import { useFCCAlertStore, useFCCMerchantStore } from '@/stores/fcc'
import { useFCCCaseStore } from '@/stores/fcc'
import { Alert, AlertHistory } from '@/types/fcc'
import AlertsFilters from '@/components/fcc/alerts/AlertsFilters.vue'
import AlertsTable from '@/components/fcc/alerts/AlertsTable.vue'
import { useFrontegg } from '@frontegg/vue'
import { successToast, errorToastWithDetails } from '@/util/toasts'
import { normalizeError } from '@/util/errorNormalizer'
import { usePerms } from '@/composables/usePerms'
import { PERMISSIONS } from '@/config/permissions'

interface Props {
  merchantId?: number
  showMerchantColumn?: boolean
  isAlertHistoryData?: boolean
  isCurrentEventsData?: boolean
  showHistoryToggle?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  merchantId: undefined,
  showMerchantColumn: true,
  isAlertHistoryData: false,
  isCurrentEventsData: false,
  showHistoryToggle: false,
})

type ViewMode = 'active' | 'all'
const viewMode = ref<ViewMode>('active')
const viewModeOptions = [
  { label: 'Active', value: 'active' as ViewMode },
  { label: 'All', value: 'all' as ViewMode },
]

const effectiveIsAlertHistory = computed(() =>
  props.showHistoryToggle ? viewMode.value === 'all' : props.isAlertHistoryData,
)

const alertStore = useFCCAlertStore()
const merchantStore = useFCCMerchantStore()
const caseStore = useFCCCaseStore()
const frontegg = useFrontegg()
const toast = useToast()
const confirm = useConfirm()
const { filteredAlerts, loading, selectedAlerts } = storeToRefs(alertStore)
const { merchantAlertHistory, alertHistoryPagination, alertHistoryLoading, nonIssueActionAvailable } =
  storeToRefs(merchantStore)

const { hasPerms } = usePerms()
const alertsTableRef = ref()

const merchantId = computed(() => props.merchantId)
const showMerchantColumn = computed(() => props.showMerchantColumn)
const canEdit = computed(() => hasPerms(PERMISSIONS.FCC_UPDATE))

// Bulk Non-Issue submission state (double-click prevention)
const isBulkNonIssueSubmitting = ref(false)

// Use historical data if the prop is true, otherwise use current alerts
const displayAlerts = computed((): (Alert | AlertHistory)[] => {
  if (effectiveIsAlertHistory.value) {
    return merchantAlertHistory.value
  } else if (props.isCurrentEventsData) {
    return merchantStore.currentMerchant?.CURRENT_EVENTS || []
  }
  return filteredAlerts.value
})

const viewModeCaption = computed(() => {
  if (viewMode.value === 'active') {
    return `Showing ${filteredAlerts.value.length} active alert${filteredAlerts.value.length === 1 ? '' : 's'}`
  }
  const total = alertHistoryPagination.value.total
  return `Showing all alerts (${total})`
})

function showApiError(err: unknown, title: string) {
  const apiError = normalizeError(err)
  errorToastWithDetails(toast, title, apiError.toToastMessage(), apiError)
}

onMounted(async () => {
  if (!hasPerms(PERMISSIONS.FCC_READ)) return
  // If we have a merchantId and this is not alert history data, fetch merchant-specific alerts
  if (
    props.merchantId !== undefined &&
    !props.isAlertHistoryData &&
    !props.isCurrentEventsData
  ) {
    try {
      await alertStore.fetchAlerts(
        frontegg.authState,
        {
          merchantId: props.merchantId,
        },
        true,
      )
    } catch (err) {
      showApiError(err, 'Failed to load alerts')
    }
  } else if (
    props.merchantId === undefined &&
    !props.isAlertHistoryData &&
    !props.isCurrentEventsData
  ) {
    // When accessed directly (no merchantId), ensure we show all alerts
    // Reset filters to clear any merchant-specific filtering
    alertStore.resetFilters()
    // Fetch all alerts if we don't have any data
    if (alertStore.alerts.length === 0 && !alertStore.loading) {
      try {
        await alertStore.fetchAlerts(frontegg.authState, undefined, true)
      } catch (err) {
        showApiError(err, 'Failed to load alerts')
      }
    }
  }
})

onUnmounted(() => {
  // When embedded with merchantId, ensure alerts selection is cleared
  if (props.merchantId !== undefined) {
    alertStore.clearSelectedAlerts()
  }
})

// Toggling between Active and All swaps the result set; clear selection so
// stale picks don't carry across views.
watch(viewMode, () => {
  alertStore.clearSelectedAlerts()
})

function openBulkActionDrawer() {
  if (alertsTableRef.value) {
    alertsTableRef.value.openBulkActionDrawer()
  }
}

function confirmBulkNonIssue() {
  const count = selectedAlerts.value.length
  confirm.require({
    header: 'Mark as Non-Issue',
    message: `Mark ${count} alert${count > 1 ? 's' : ''} as Non-Issue? This closes ${count > 1 ? 'them' : 'it'} with no further action.`,
    icon: 'pi pi-check-circle',
    rejectProps: { label: 'Cancel', outlined: true },
    acceptProps: {
      label: 'Mark as Non-Issue',
      'data-test-id': 'non-issue-confirm-accept',
    },
    accept: () => handleBulkNonIssue(),
    reject: () => {},
  })
}

async function handleBulkNonIssue() {
  // Atomic double-click prevention
  const wasAlreadyProcessing = isBulkNonIssueSubmitting.value
  isBulkNonIssueSubmitting.value = true
  if (wasAlreadyProcessing) return
  const alertIds = selectedAlerts.value.map(a => a.ALERT_ID)
  const alertCount = alertIds.length
  try {
    await merchantStore.dispositionAsNonIssue(frontegg.authState, alertIds)
    // Fire-and-forget cases refresh for audit trail parity with drawer flow
    void caseStore.refreshCases(frontegg.authState)
    alertStore.clearSelectedAlerts()
    await handleActionCompleted(alertCount)
  } catch (err) {
    const apiError = normalizeError(err)
    errorToastWithDetails(
      toast,
      'Failed to mark as Non-Issue',
      apiError.toToastMessage(),
      apiError,
    )
  } finally {
    isBulkNonIssueSubmitting.value = false
  }
}

// Pagination event handlers for alert history
async function onPageChange(page: number) {
  if (effectiveIsAlertHistory.value && props.merchantId) {
    try {
      await merchantStore.fetchMerchantAlertHistory(
        frontegg.authState,
        props.merchantId,
        page,
        alertHistoryPagination.value.pageSize,
      )
    } catch (err) {
      // Silently handle pagination errors — user can retry by clicking again
      console.warn('Failed to load alert history page:', err)
    }
  }
}

async function onPageSizeChange(pageSize: number) {
  if (effectiveIsAlertHistory.value && props.merchantId) {
    try {
      await merchantStore.fetchMerchantAlertHistory(
        frontegg.authState,
        props.merchantId,
        1, // Reset to first page when changing page size
        pageSize,
      )
    } catch (err) {
      // Silently handle pagination errors — user can retry by clicking again
      console.warn('Failed to load alert history page:', err)
    }
  }
}

// Handle action completion - show success toast and refresh
async function handleActionCompleted(alertCount: number) {
  // Show success toast
  successToast(
    toast,
    'Action Completed',
    `Successfully actioned ${alertCount} alert${alertCount > 1 ? 's' : ''}`,
  )

  // Refresh the alerts list
  await handleRefresh()
}

// Handle refresh based on context
async function handleRefresh() {
  try {
    if (effectiveIsAlertHistory.value && props.merchantId) {
      // Refresh alert history for specific merchant
      await merchantStore.fetchMerchantAlertHistory(
        frontegg.authState,
        props.merchantId,
        alertHistoryPagination.value.page,
        alertHistoryPagination.value.pageSize,
      )
    } else if (props.isCurrentEventsData && props.merchantId) {
      // Refresh current events for specific merchant
      await merchantStore.fetchMerchantDetails(
        frontegg.authState,
        props.merchantId,
      )
    } else if (props.merchantId) {
      // Refresh alerts for specific merchant
      await alertStore.fetchAlerts(frontegg.authState, {
        merchantId: props.merchantId,
      })
    } else {
      // Refresh all alerts (default behavior)
      await alertStore.fetchAlerts(frontegg.authState)
    }
  } catch (err) {
    showApiError(err, 'Failed to refresh alerts')
  }
}
</script>
